import { useCallback, useEffect, useRef, useState } from "react";
import {
  applyPageHtmlTools,
  type PageHtmlToolCall,
} from "@/utils/applyPageHtmlTools";
import * as prettier from "prettier/standalone";
import * as prettierPluginHtml from "prettier/plugins/html";
import { Button } from "@/components/ui/button";
import { useSessionSuperuser } from "@/hooks/use-session-superuser";
import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp, Loader2, Send } from "lucide-react";

type ChatMessage = {
  role: "user" | "assistant";
  content: string;
  durationMs?: number;
  inputTokens?: number;
  outputTokens?: number;
  costUsd?: number;
};

type QueryUsage = {
  inputTokens: number;
  outputTokens: number;
  costUsd: number;
};

const PAGE_HTML_MODEL = "claude-sonnet-5";

const MIN_PROMPT_BALANCE_USD = 5;

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms} ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)} s`;
  return `${(ms / 60_000).toFixed(1)} min`;
}

function formatTokenCount(count: number): string {
  if (count >= 1_000_000) return `${(count / 1_000_000).toFixed(1)}M`;
  if (count >= 10_000) return `${(count / 1_000).toFixed(1)}k`;
  return count.toLocaleString();
}

function formatCostUsd(usd: number): string {
  if (usd < 0.01) return `$${usd.toFixed(4)}`;
  if (usd < 1) return `$${usd.toFixed(3)}`;
  return `$${usd.toFixed(2)}`;
}

function formatBalanceUsd(usd: number): string {
  return `$${usd.toFixed(2)}`;
}

function QueryStatsLabel({
  durationMs,
  inputTokens,
  outputTokens,
  costUsd,
}: {
  durationMs?: number;
  inputTokens?: number;
  outputTokens?: number;
  costUsd?: number;
}) {
  const hasTokens = inputTokens != null && outputTokens != null;
  const hasDuration = durationMs != null;
  const hasCost = costUsd != null;

  if (!hasDuration && !hasTokens && !hasCost) return <>—</>;

  return (
    <>
      {hasDuration && formatDuration(durationMs)}
      {hasDuration && hasTokens && " · "}
      {hasTokens && (
        <span
          title={`${inputTokens.toLocaleString()} in · ${outputTokens.toLocaleString()} out`}
          className="underline decoration-dotted underline-offset-2"
        >
          {formatTokenCount(inputTokens + outputTokens)} tokens
        </span>
      )}
      {(hasDuration || hasTokens) && hasCost && " · "}
      {hasCost && formatCostUsd(costUsd)}
    </>
  );
}

interface PageHtmlChatPanelProps {
  html: string;
  onHtmlChange: (html: string) => void;
  onSave?: (html?: string) => void;
  onGenerationComplete?: () => void;
}

export function PageHtmlChatPanel({
  html,
  onHtmlChange,
  onSave,
  onGenerationComplete,
}: PageHtmlChatPanelProps) {
  const [prompt, setPrompt] = useState("");
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [streamingContent, setStreamingContent] = useState<string | null>(null);
  const [streamingStartTime, setStreamingStartTime] = useState<number | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);
  const [isChatPanelOpen, setIsChatPanelOpen] = useState(true);
  const [expandedAssistantIndices, setExpandedAssistantIndices] = useState<Set<number>>(new Set());
  const [streamingExpanded, setStreamingExpanded] = useState(false);
  const [balanceUsd, setBalanceUsd] = useState<number | null>(null);
  const [, setTick] = useState(0);
  const { isSuperuser, sessionLoading } = useSessionSuperuser();

  const isBelowMinimumBalance =
    !isSuperuser &&
    !sessionLoading &&
    balanceUsd != null &&
    balanceUsd < MIN_PROMPT_BALANCE_USD;

  const fetchBalance = useCallback(async () => {
    try {
      const res = await fetch("/api/billing");
      if (!res.ok) return;
      const data = (await res.json()) as { billing?: { balance?: number } | null };
      setBalanceUsd(data.billing?.balance ?? 0);
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    void fetchBalance();
  }, [fetchBalance]);

  useEffect(() => {
    if (!streamingStartTime || !isGenerating) return;
    const id = setInterval(() => setTick((t) => t + 1), 200);
    return () => clearInterval(id);
  }, [streamingStartTime, isGenerating]);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, streamingContent]);

  const handleGenerate = useCallback(async () => {
    if (!prompt.trim() || isGenerating || isBelowMinimumBalance) return;
    const userPrompt = prompt.trim();
    setPrompt("");
    setIsGenerating(true);
    setGenerateError(null);
    setMessages((prev) => [...prev, { role: "user", content: userPrompt }]);
    setStreamingContent("");
    const startTime = performance.now();
    setStreamingStartTime(startTime);
    try {
      const res = await fetch("/api/pages/generate-html", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          prompt: userPrompt,
          html,
          model: PAGE_HTML_MODEL,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error ?? "Failed to generate");
      }
      const reader = res.body?.getReader();
      if (!reader) throw new Error("No response body");
      const decoder = new TextDecoder();
      let accumulated = "";
      let lineBuffer = "";
      let queryUsage: QueryUsage | null = null;
      let toolCalls: PageHtmlToolCall[] = [];
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        lineBuffer += decoder.decode(value, { stream: true });
        const lines = lineBuffer.split("\n");
        lineBuffer = lines.pop() ?? "";
        for (const line of lines) {
          if (line.startsWith("data: ")) {
            const data = line.slice(6);
            if (data === "[DONE]") continue;
            try {
              const parsed = JSON.parse(data) as {
                fanrank_usage?: QueryUsage;
                fanrank_tools?: PageHtmlToolCall[];
                choices?: Array<{ delta?: { content?: string } }>;
              };
              if (parsed.fanrank_usage) {
                queryUsage = parsed.fanrank_usage;
                continue;
              }
              if (parsed.fanrank_tools) {
                toolCalls = parsed.fanrank_tools;
                continue;
              }
              const content = parsed.choices?.[0]?.delta?.content;
              if (content) {
                accumulated += content;
                setStreamingContent(accumulated);
              }
            } catch {
              // ignore parse errors for partial chunks
            }
          }
        }
      }
      if (lineBuffer.trim()) {
        const line = lineBuffer;
        if (line.startsWith("data: ")) {
          const data = line.slice(6);
          if (data !== "[DONE]") {
            try {
              const parsed = JSON.parse(data) as {
                fanrank_usage?: QueryUsage;
                fanrank_tools?: PageHtmlToolCall[];
                choices?: Array<{ delta?: { content?: string } }>;
              };
              if (parsed.fanrank_usage) {
                queryUsage = parsed.fanrank_usage;
              } else if (parsed.fanrank_tools) {
                toolCalls = parsed.fanrank_tools;
              } else {
                const content = parsed.choices?.[0]?.delta?.content;
                if (content) {
                  accumulated += content;
                }
              }
            } catch {
              // ignore incomplete final chunk
            }
          }
        }
      }
      setStreamingContent(null);
      setStreamingStartTime(null);
      setStreamingExpanded(false);
      const durationMs = Math.round(performance.now() - startTime);
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: accumulated,
          durationMs,
          inputTokens: queryUsage?.inputTokens,
          outputTokens: queryUsage?.outputTokens,
          costUsd: queryUsage?.costUsd,
        },
      ]);
      if (toolCalls.length > 0) {
        const nextHtml = applyPageHtmlTools({ html, toolCalls });
        if (nextHtml !== html) {
          const formatted = await prettier
            .format(nextHtml, { parser: "html", plugins: [prettierPluginHtml] })
            .catch(() => nextHtml);
          onHtmlChange(formatted);
          onSave?.(formatted);
        }
      }
      onGenerationComplete?.();
      void fetchBalance();
    } catch (e) {
      setStreamingContent(null);
      setStreamingStartTime(null);
      setStreamingExpanded(false);
      setGenerateError(e instanceof Error ? e.message : "Failed to generate");
    } finally {
      setIsGenerating(false);
    }
  }, [prompt, html, onHtmlChange, onSave, onGenerationComplete, isGenerating, isBelowMinimumBalance, fetchBalance]);

  return (
    <div
      className={`shrink-0 border-l border-gray-200 flex flex-col min-h-0 transition-[width] duration-200 ${
        isChatPanelOpen ? "w-72" : "w-10"
      }`}
    >
      {isChatPanelOpen ? (
        <>
          <div className="shrink-0 flex justify-end px-2 py-1 border-b border-gray-200">
            <button
              type="button"
              onClick={() => setIsChatPanelOpen(false)}
              className="p-1 rounded hover:bg-gray-50 text-gray-500"
              title="Collapse chat"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
          <div className="flex-1 overflow-auto p-3 space-y-3 min-h-0">
            {messages.map((msg, i) => (
              <div
                key={i}
                className={`rounded-lg px-3 py-2 text-sm ${
                  msg.role === "user"
                    ? "bg-blue-100 text-blue-900 ml-4"
                    : "bg-gray-100 text-gray-900 mr-4"
                }`}
              >
                {msg.role === "assistant" ? (
                  <>
                    <button
                      type="button"
                      onClick={() =>
                        setExpandedAssistantIndices((prev) => {
                          const next = new Set(prev);
                          if (next.has(i)) next.delete(i);
                          else next.add(i);
                          return next;
                        })
                      }
                      className="flex items-center gap-1.5 w-full text-left text-muted-foreground hover:text-foreground"
                    >
                      {expandedAssistantIndices.has(i) ? (
                        <ChevronUp className="h-3.5 w-3.5 shrink-0" />
                      ) : (
                        <ChevronDown className="h-3.5 w-3.5 shrink-0" />
                      )}
                      <span className="text-xs font-medium">
                        <QueryStatsLabel
                          durationMs={msg.durationMs}
                          inputTokens={msg.inputTokens}
                          outputTokens={msg.outputTokens}
                          costUsd={msg.costUsd}
                        />
                      </span>
                    </button>
                    {expandedAssistantIndices.has(i) && (
                      <pre className="whitespace-pre-wrap font-sans break-words mt-2 pt-2 border-t border-gray-200">
                        {msg.content}
                      </pre>
                    )}
                  </>
                ) : (
                  <pre className="whitespace-pre-wrap font-sans break-words">{msg.content}</pre>
                )}
              </div>
            ))}
            {streamingContent !== null && (
              <div className="rounded-lg px-3 py-2 text-sm bg-gray-100 text-gray-900 mr-4">
                <button
                  type="button"
                  onClick={() => setStreamingExpanded((e) => !e)}
                  className="flex items-center gap-1.5 w-full text-left text-muted-foreground hover:text-foreground"
                >
                  {streamingExpanded ? (
                    <ChevronUp className="h-3.5 w-3.5 shrink-0" />
                  ) : (
                    <ChevronDown className="h-3.5 w-3.5 shrink-0" />
                  )}
                  <span className="text-xs font-medium">
                    {streamingStartTime != null ? (
                      <QueryStatsLabel
                        durationMs={Math.round(performance.now() - streamingStartTime)}
                      />
                    ) : (
                      "..."
                    )}
                  </span>
                </button>
                {streamingExpanded && (
                  <pre className="whitespace-pre-wrap font-sans break-words mt-2 pt-2 border-t border-gray-200">
                    {streamingContent || "..."}
                  </pre>
                )}
              </div>
            )}
            {generateError && <p className="text-sm text-red-600">{generateError}</p>}
            {balanceUsd != null && isBelowMinimumBalance && (
              <p className="text-sm text-amber-700">
                At least ${MIN_PROMPT_BALANCE_USD} balance required to start a prompt. Current
                balance: {formatBalanceUsd(balanceUsd)}.
              </p>
            )}
            <div ref={messagesEndRef} />
          </div>
          <div className="shrink-0 border-t border-gray-200 p-3 space-y-2">
            <div className="flex gap-2">
              <input
                type="text"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleGenerate()}
                className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 min-w-0"
                disabled={isGenerating || isBelowMinimumBalance}
                placeholder={
                  isBelowMinimumBalance
                    ? `Requires $${MIN_PROMPT_BALANCE_USD} balance`
                    : undefined
                }
              />
              <Button
                type="button"
                size="icon"
                onClick={handleGenerate}
                disabled={!prompt.trim() || isGenerating || isBelowMinimumBalance}
                className="h-9 w-9 shrink-0"
              >
                {isGenerating ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>
        </>
      ) : (
        <div className="flex flex-col items-center pt-2">
          <button
            type="button"
            onClick={() => setIsChatPanelOpen(true)}
            className="p-1 rounded hover:bg-gray-50 text-gray-500"
            title="Expand chat"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
}
